import { forwardRef, useCallback, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"

// --- Hooks ---
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Icons ---
import { CornerDownLeftIcon } from "@/tiptap/icons/corner-down-left-icon"
import { ExternalLinkIcon } from "@/tiptap/icons/external-link-icon"
import { LinkIcon } from "@/tiptap/icons/link-icon"
import { TrashIcon } from "@/tiptap/icons/trash-icon"

import { LINK_SHORTCUT_KEY, useLinkPopover } from "@/tiptap/ui/link-popover"

import { Button } from "@/tiptap/ui-primitive/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/tiptap/ui-primitive/popover"
import { Separator } from "@/tiptap/ui-primitive/separator"
import {
  Card,
  CardBody,
  CardItemGroup,
} from "@/tiptap/ui-primitive/card"
import { Input } from "@/tiptap/ui-primitive/input"
import { ButtonGroup } from "@/tiptap/ui-primitive/button-group"


/**
 * Link button component for triggering the link popover
 */
export const LinkButton = forwardRef(({ className, children, ...props }, ref) => {
  return (
    <Button
      type="button"
      className={className}
      variant="ghost"
      role="button"
      tabIndex={-1}
      aria-label="Link"
      tooltip="Link"
      ref={ref}
      {...props}>
      {children || <LinkIcon className="tiptap-button-icon" />}
    </Button>
  );
})

LinkButton.displayName = "LinkButton"

/**
 * Main content component for the link popover
 */
const LinkMain = ({
  url,
  setUrl,
  setLink,
  removeLink,
  openLink,
  isActive,
}) => {
  const isMobile = useIsBreakpoint()

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      setLink()
    }
  }

  return (
    <Card
      style={{
        ...(isMobile ? { boxShadow: "none", border: 0 } : {}),
      }}>
      <CardBody
        style={{
          ...(isMobile ? { padding: 0 } : {}),
        }}>
        <CardItemGroup orientation="horizontal">
          <Input
            type="url"
            placeholder="Paste a link..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            className="tiptap-link-input" />

          <ButtonGroup>
            <Button
              type="button"
              onClick={setLink}
              title="Apply link"
              disabled={!url && !isActive}
              variant="ghost">
              <CornerDownLeftIcon className="tiptap-button-icon" />
            </Button>
          </ButtonGroup>

          <Separator />

          <ButtonGroup>
            <ButtonGroup>
              <Button
                type="button"
                onClick={openLink}
                title="Open in new window"
                disabled={!url && !isActive}
                variant="ghost">
                <ExternalLinkIcon className="tiptap-button-icon" />
              </Button>
            </ButtonGroup>

            <ButtonGroup>
              <Button
                type="button"
                onClick={removeLink}
                title="Remove link"
                disabled={!url && !isActive}
                variant="ghost">
                <TrashIcon className="tiptap-button-icon" />
              </Button>
            </ButtonGroup>
          </ButtonGroup>
        </CardItemGroup>
      </CardBody>
    </Card>
  );
}

/**
 * Link content component for standalone use
 */
export const LinkContent = ({ editor }) => {
  const linkPopover = useLinkPopover({
    editor,
  })

  return <LinkMain {...linkPopover} />;
}

/**
 * Link popover component for Tiptap editors.
 *
 * For custom popover implementations, use the `useLinkPopover` hook instead.
 */
export const LinkPopover = forwardRef((
  {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onSetLink,
    onOpenChange,
    autoOpenOnLinkActive = true,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()
  const [isOpen, setIsOpen] = useState(false)

  const {
    isVisible,
    canSet,
    isActive,
    url,
    setUrl,
    setLink,
    removeLink,
    openLink,
    label,
    shortcutKeys,
    Icon,
  } = useLinkPopover({
    editor,
    hideWhenUnavailable,
    onSetLink,
  })

  useHotkeys(LINK_SHORTCUT_KEY, (event) => {
    event.preventDefault()
    setIsOpen(true)
  }, {
    enabled: isVisible && canSet,
    enableOnContentEditable: !isMobile,
    enableOnFormTags: true,
  })

  const handleOnOpenChange = useCallback((nextIsOpen) => {
    setIsOpen(nextIsOpen)
    onOpenChange?.(nextIsOpen)
  }, [onOpenChange])

  const handleSetLink = useCallback(() => {
    setLink()
    setIsOpen(false)
  }, [setLink])

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    setIsOpen(!isOpen)
  }, [onClick, isOpen])

  useEffect(() => {
    if (autoOpenOnLinkActive && isActive) {
      setIsOpen(true)
    }
  }, [autoOpenOnLinkActive, isActive])

  if (!isVisible) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOnOpenChange}>
      <PopoverTrigger asChild>
        <LinkButton
          disabled={!canSet}
          data-active-state={isActive ? "on" : "off"}
          data-disabled={!canSet}
          aria-label={label}
          aria-pressed={isActive}
          shortcutKeys={shortcutKeys}
          onClick={handleClick}
          {...buttonProps}
          ref={ref}>
          {children ?? <Icon className="tiptap-button-icon" />}
        </LinkButton>
      </PopoverTrigger>
      <PopoverContent>
        <LinkMain
          url={url}
          setUrl={setUrl}
          setLink={handleSetLink}
          removeLink={removeLink}
          openLink={openLink}
          isActive={isActive} />
      </PopoverContent>
    </Popover>
  );
})

LinkPopover.displayName = "LinkPopover"

export default LinkPopover
